<template>
  <div
    ref="parallaxContainerRef"
    v-editable="blok"
    class="CategoryVisualListingBlock layout-container"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div class="flex flex-row-reverse desk:flex-row items-center gap-12">
      <div class="w-full relative">
        <div class="w-full aspect-portrait overflow-hidden">
          <nuxt-img
            :style="blok.parallaxEffect ? { transform: `translateY(${offset}px) scale(1.2)` } : {}"
            preset="standard"
            loading="lazy"
            sizes="mob375:100vw mob390:100vw mob414:100vw desk:1260px"
            :src="hoverItem"
            class="w-full h-full object-cover transition-transform duration-[50ms]"
            :class="focalPointClass"
          />
        </div>
        <div
          v-if="hoverItemSecond"
          class="w-1/3 hidden desk:block absolute -right-1/4 -translate-x-half bottom-64 aspect-portrait"
        >
          <nuxt-img
            :style="blok.parallaxEffect ? { transform: `translateY(${reverseOffset}px)` } : {}"
            preset="standard"
            loading="lazy"
            sizes="mob375:100vw mob390:100vw mob414:100vw desk:252px"
            :src="hoverItemSecond"
            class="w-full h-full object-cover transition-transform duration-[50ms]"
            :class="secondFocalPointClass"
          />
        </div>
      </div>
      <div class="flex flex-col desk:items-center w-full">
        <ul class="flex flex-col gap-16 desk:gap-20">
          <li class="type-tagline-sm mb-4 desk:mb-8">{{ blok.title }}</li>
          <li
            v-for="(item, index) in blok.listItem"
            :key="item._uid"
            @mouseenter="handleHover(item, index)"
          >
            <nuxt-link
              :to="$sbLinkFix(item.link)"
              :target="$sbLinkTarget(item.link)"
            >
              <div
                class="type-headline-2xl desk:type-headline-3xl"
                :class="{
                  'text-text-base underline underline-offset-4	decoration-1': activeIndex === index,
                  'text-text-inactive': activeIndex !== index
                }"
              >
                <div
                  class="inline pb-4"
                  :class="{ 'border-active': activeIndex === index }"
                >
                  {{ item.category }}
                </div>
                <sup>{{ item.amount }}</sup>
              </div>
            </nuxt-link>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  DatasourceObjectPosition,
  StoryBlokAsset,
  StoryBlokLink,
  StoryBlokPropBase
} from '~/constants/types/storyblok';
import useDesktopWidth from '~/composeables/useVisualAppearancePlugin';
import { computed, ref } from 'vue';
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import { useElementBounding } from '@vueuse/core';

type Item = StoryBlokPropBase & {
  image: StoryBlokAsset;
  focalPoint: DatasourceObjectPosition;
  secondImage: StoryBlokAsset;
  secondFocalPoint: DatasourceObjectPosition;
  category: string;
  amount: string;
  link: StoryBlokLink;
}

type Props = StoryBlokPropBase & {
  title: string;
  listItem: Item[];
  parallaxEffect: boolean;
}

const props = defineProps<{
  blok: Props,
}>();

const hoverItem = ref(props.blok.listItem[0]?.image?.filename);
const hoverItemSecond = ref(props.blok.listItem[0]?.secondImage?.filename || props.blok.listItem[0]?.secondImage?.filename);
const activeIndex = ref(0);

const handleHover = (item: Item, index: number) => {
  hoverItem.value = item.image.filename;
  hoverItemSecond.value = item.secondImage?.filename || null;
  activeIndex.value = index;
};

const focalPointClass = computed(() => {
  const focalPoint = props.blok.listItem[activeIndex.value]?.focalPoint;
  return focalPoint ? `${focalPoint}` : '';
});

const secondFocalPointClass = computed(() => {
  const secondFocalPoint = props.blok.listItem[activeIndex.value]?.secondFocalPoint;
  return secondFocalPoint ? `${secondFocalPoint}` : '';
});

const parallaxContainerRef = useTemplateRef('parallaxContainerRef');
const { top } = useElementBounding(parallaxContainerRef);

const windowHeight = ref(0);
onMounted(()=> {
  windowHeight.value = window.innerHeight;
});

const progress = computed(() => {
  const start = windowHeight.value;
  const end = -400;
  return Math.max(0, Math.min((start - top.value) / (start - end), 1));
});

const offset = computed(() => progress.value * 80);
const reverseOffset = computed(() => -offset.value);

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);

</script>
